import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores'
import {
  programService,
  type PublicProgramWithAuthor,
  type PublicProgramFilters,
} from '../../services'
import { PublicProgramCard } from '../../components/program'
import { Button, Input } from '../../components/common'
import { useToast } from '../../components/common/Toast'

type SortOption = 'newest' | 'popular' | 'most_duplicated'

export function PublicProgramsPage() {
  const navigate = useNavigate()
  const { profile } = useAuthStore()
  const toast = useToast()

  const [programs, setPrograms] = useState<PublicProgramWithAuthor[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [actionLoadingIds, setActionLoadingIds] = useState<Set<string>>(
    new Set()
  )

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('newest')

  const ITEMS_PER_PAGE = 12

  const loadPrograms = useCallback(async () => {
    setIsLoading(true)
    try {
      const filters: PublicProgramFilters = {
        search: searchQuery || undefined,
        category: selectedCategory || undefined,
        difficulty: selectedDifficulty || undefined,
        sortBy,
      }

      const result = await programService.browsePublicPrograms(
        filters,
        profile?.id,
        ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE
      )

      setPrograms(result.programs)
      setTotal(result.total)
    } catch (error) {
      console.error('Failed to load public programs:', error)
      toast.error('Failed to load programs')
    } finally {
      setIsLoading(false)
    }
  }, [searchQuery, selectedCategory, selectedDifficulty, sortBy, page, profile?.id])

  const loadCategories = useCallback(async () => {
    try {
      const cats = await programService.getPublicCategories()
      setCategories(cats)
    } catch (error) {
      console.error('Failed to load categories:', error)
    }
  }, [])

  useEffect(() => {
    loadPrograms()
  }, [loadPrograms])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  // Reset page when filters change
  useEffect(() => {
    setPage(0)
  }, [searchQuery, selectedCategory, selectedDifficulty, sortBy])

  const handleLike = async (program: PublicProgramWithAuthor) => {
    if (!profile?.id) {
      toast.error('Please sign in to like programs')
      return
    }

    setActionLoadingIds((prev) => new Set(prev).add(`like-${program.id}`))
    try {
      const newLikedState = await programService.toggleLike(
        program.id,
        profile.id,
        program.is_liked || false
      )

      // Update local state
      setPrograms((prev) =>
        prev.map((p) =>
          p.id === program.id
            ? {
                ...p,
                is_liked: newLikedState,
                like_count: newLikedState
                  ? (p.like_count || 0) + 1
                  : Math.max((p.like_count || 0) - 1, 0),
              }
            : p
        )
      )
    } catch (error) {
      toast.error('Failed to update like')
    } finally {
      setActionLoadingIds((prev) => {
        const next = new Set(prev)
        next.delete(`like-${program.id}`)
        return next
      })
    }
  }

  const handleSave = async (program: PublicProgramWithAuthor) => {
    if (!profile?.id) {
      toast.error('Please sign in to save programs')
      return
    }

    setActionLoadingIds((prev) => new Set(prev).add(`save-${program.id}`))
    try {
      const newSavedState = await programService.toggleSave(
        program.id,
        profile.id,
        program.is_saved || false
      )

      // Update local state
      setPrograms((prev) =>
        prev.map((p) =>
          p.id === program.id ? { ...p, is_saved: newSavedState } : p
        )
      )

      toast.success(newSavedState ? 'Program saved' : 'Program unsaved')
    } catch (error) {
      toast.error('Failed to update save')
    } finally {
      setActionLoadingIds((prev) => {
        const next = new Set(prev)
        next.delete(`save-${program.id}`)
        return next
      })
    }
  }

  const handleDuplicate = async (program: PublicProgramWithAuthor) => {
    if (!profile?.id) {
      toast.error('Please sign in to use this program')
      return
    }

    if (profile.subscription_tier === 'free') {
      toast.error('Upgrade to duplicate programs')
      navigate('/dashboard/subscription')
      return
    }

    try {
      const newProgram = await programService.duplicatePublicProgram(
        program.id,
        profile.id
      )

      if (newProgram) {
        toast.success('Program added to your library!')
        navigate(`/dashboard/programs/${newProgram.id}`)
      }
    } catch (error) {
      toast.error('Failed to duplicate program')
    }
  }

  const handleViewDetails = (program: PublicProgramWithAuthor) => {
    // Navigate to a public program detail page (could be implemented later)
    // For now, just duplicate if they want to use it
    navigate(`/dashboard/public-programs/${program.id}`)
  }

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE)

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Browse Programs</h1>
        <p className="mt-1 text-gray-600">
          Discover exercise programs shared by coaches in the community
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <Input
              placeholder="Search programs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Category */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>

          {/* Difficulty */}
          <select
            value={selectedDifficulty}
            onChange={(e) => setSelectedDifficulty(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
            <option value="expert">Expert</option>
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="newest">Newest</option>
            <option value="popular">Most Liked</option>
            <option value="most_duplicated">Most Used</option>
          </select>
        </div>
      </div>

      {/* Results count */}
      <div className="mb-4 text-sm text-gray-500">
        {isLoading ? (
          'Loading...'
        ) : (
          <>
            Showing {programs.length} of {total} programs
          </>
        )}
      </div>

      {/* Programs Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gray-200" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-1" />
                  <div className="h-3 bg-gray-200 rounded w-16" />
                </div>
              </div>
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-full mb-4" />
              <div className="flex gap-2">
                <div className="h-6 bg-gray-200 rounded w-16" />
                <div className="h-6 bg-gray-200 rounded w-16" />
              </div>
            </div>
          ))}
        </div>
      ) : programs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <svg
            className="w-16 h-16 text-gray-300 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <p className="text-gray-500">No programs found</p>
          <p className="text-sm text-gray-400 mt-1">
            Try adjusting your filters
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((program) => (
            <PublicProgramCard
              key={program.id}
              program={program}
              onLike={() => handleLike(program)}
              onSave={() => handleSave(program)}
              onDuplicate={() => handleDuplicate(program)}
              onViewDetails={() => handleViewDetails(program)}
              isLiking={actionLoadingIds.has(`like-${program.id}`)}
              isSaving={actionLoadingIds.has(`save-${program.id}`)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-sm text-gray-600">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
